/**
 * The recent microphone audio (PCM16), addressed by its position in the
 * recording (in samples, paused time excluded). The live transcript uses it
 * to send again what the transcriber may not have heard after a lost
 * connection (screen lock, page freeze, network drop).
 *
 * Holds at most `capacity` samples; older audio is dropped first, and
 * `start` moves forward. A read from before `start` cannot be served.
 */
export class PcmHistory {
  private chunks: Int16Array[] = [];
  private startSample = 0;
  private endSample = 0;

  public constructor(private readonly capacity: number) {}

  /** Position of the oldest held sample. */
  public get start(): number {
    return this.startSample;
  }

  /** Position just after the newest sample (the recording so far). */
  public get end(): number {
    return this.endSample;
  }

  public append(pcm: Int16Array): void {
    if (pcm.length === 0) return;
    this.chunks.push(pcm);
    this.endSample += pcm.length;
    const excess = this.endSample - this.startSample - this.capacity;
    if (excess > 0) this.trimBefore(this.startSample + excess);
  }

  /** Forgets everything before `position`. */
  public trimBefore(position: number): void {
    let target = Math.min(position, this.endSample);
    while (target > this.startSample && this.chunks.length) {
      const first = this.chunks[0] as Int16Array;
      const drop = target - this.startSample;
      if (drop >= first.length) {
        this.chunks.shift();
        this.startSample += first.length;
      } else {
        this.chunks[0] = first.subarray(drop);
        this.startSample += drop;
        target = this.startSample;
      }
    }
  }

  /**
   * Skips ahead: the next appended sample is at `position` (audio between
   * was never captured). Everything held before is forgotten.
   */
  public jumpTo(position: number): void {
    this.chunks = [];
    this.startSample = position;
    this.endSample = position;
  }

  /** Samples [from, to), or null when part of it is no longer held. */
  public slice(from: number, to: number): Int16Array | null {
    if (from < this.startSample || to > this.endSample || to < from)
      return null;
    const out = new Int16Array(to - from);
    let position = this.startSample;
    let written = 0;
    for (const chunk of this.chunks) {
      const chunkEnd = position + chunk.length;
      if (chunkEnd > from && position < to) {
        const begin = Math.max(from, position) - position;
        const finish = Math.min(to, chunkEnd) - position;
        out.set(chunk.subarray(begin, finish), written);
        written += finish - begin;
      }
      position = chunkEnd;
      if (position >= to) break;
    }
    return out;
  }

  public reset(): void {
    this.jumpTo(0);
  }
}
